import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/founder", label: "Founder" },
  { to: "/basketball-camp", label: "Basketball" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b-2 ${
        scrolled
          ? "bg-[#F4EBDC] border-[#0E0E0E]"
          : "bg-[#F4EBDC]/90 backdrop-blur border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 flex items-center justify-between h-16 sm:h-20">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-3 group"
        >
          <span className="relative flex items-center justify-center w-11 h-11 bg-[#1B4F72] border-2 border-[#0E0E0E]">
            <img
              src="/assets/logo/divine-care-logo.png"
              alt="Divine Care"
              className="h-9 w-9 object-contain"
            />
          </span>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-2xl sm:text-3xl lg:text-[34px] text-[#0E0E0E] tracking-tight leading-none">
              DIVINE CARE ORGANIZATION
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-mono text-xs tracking-[0.18em] uppercase relative transition-colors ${
                  isActive
                    ? "text-[#1B4F72]"
                    : "text-[#0E0E0E] hover:text-[#1B4F72]"
                }`
              }
              end={l.to === "/"}
            >
              <span className="text-[#1B4F72] mr-2">0{i + 1}</span>
              {l.label}
            </NavLink>
          ))}
          <a
            href="/#contact"
            data-testid="navbar-contact-cta"
            className="btn-base btn-orange text-[10px] py-3 px-4"
          >
            Contact ↘
          </a>
        </nav>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-11 h-11 bg-[#0E0E0E] text-[#F4EBDC]"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#0E0E0E] text-[#F4EBDC] border-t-2 border-[#0E0E0E]"
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-3xl ${
                    isActive ? "text-[#1B4F72]" : "text-[#F4EBDC]"
                  }`
                }
                end={l.to === "/"}
              >
                <span className="text-[#1B4F72] mr-3 font-mono text-base">
                  0{i + 1}
                </span>
                {l.label}
              </NavLink>
            ))}
            <a
              href="/#contact"
              data-testid="mobile-navbar-contact-cta"
              className="btn-base btn-orange mt-4 self-start"
            >
              Contact ↘
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
